import React, { useState, useEffect, useCallback } from 'react';
import axios from './../axios';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Paper,
    Chip,
    Alert,
    Pagination,
    Typography,
    Box,
    TextField
} from '@mui/material';
import Spinner from './Spinner';

const CreditList = ({ searchWord = '' }) => {
    const [credits, setCredits] = useState([]);
    const [allCredits, setAllCredits] = useState([]); // Store all credits for search
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedCredit, setSelectedCredit] = useState(null);
    const [delOpen, setDelOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10); // 10 items per page
    const [confirmationText, setConfirmationText] = useState('');

    const fetchCredits = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get('/credit'); // Fetch all credits
            const creditsData = response.data.data || [];
            setCredits(creditsData);
            setAllCredits(creditsData);
        } catch (err) {
            setError('Failed to fetch credits');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCredits();
    }, [fetchCredits]);

    // Search credits when searchWord changes
    const getSearchCredits = useCallback(() => {
        if (!searchWord.trim()) {
            setCredits(allCredits);
        } else {
            const lowerSearchWord = searchWord.toLowerCase();
            const filtered = allCredits.filter((credit) => {
                const name = credit.name?.toLowerCase() || '';
                const address = credit.address?.toLowerCase() || '';
                const amount = credit.amount?.toString().toLowerCase() || '';
                const creditType = credit.creditType?.toLowerCase() || '';
                const status = credit.status?.toLowerCase() || '';
                return (
                    name.includes(lowerSearchWord) ||
                    address.includes(lowerSearchWord) ||
                    amount.includes(lowerSearchWord) ||
                    creditType.includes(lowerSearchWord) ||
                    status.includes(lowerSearchWord)
                );
            });
            setCredits(filtered);
        }
        setCurrentPage(1); // Reset to first page when searching
    }, [searchWord, allCredits]);

    useEffect(() => {
        getSearchCredits();
    }, [getSearchCredits]);

    // Calculate pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentCredits = credits.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(credits.length / itemsPerPage);

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    const handleDelete = async () => {
        if (!selectedCredit) return; // Safety check
        try {
            setDeleting(true);
            await axios.delete(`/credit/${selectedCredit._id}`);
            setDelOpen(false);
            fetchCredits(); // Refresh the list
        } catch {
            setError('Failed to delete credit');
        } finally {
            setDeleting(false);
        }
    };

    const handleStatusChange = async (newStatus) => {
        if (!selectedCredit) return; // Safety check
        try {
            await axios.put(`/credit/${selectedCredit._id}`, { status: newStatus });
            setDetailsOpen(false);
            fetchCredits(); // Refresh the list
        } catch {
            setError('Failed to update credit status');
        }
    };

    // Table styling
    const tableCellStyle = {
        fontWeight: 600,
        fontSize: '12px',
        color: '#ffffff',
        textTransform: 'uppercase',
        padding: '12px 16px'
    };

    const customTableCellStyle = {
        padding: '12px 16px',
        fontSize: '13px',
        fontWeight: 500,
        color: '#374151',
        borderBottom: '1px solid #f3f4f6',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    };

    const getStatusColor = (status) => {
        const statusLower = status?.toLowerCase();
        switch (statusLower) {
            case 'active':
            case 'approved':
            case 'disbursed':
                return {
                    backgroundColor: '#dcfce7',
                    color: '#059669',
                    border: '1px solid #bbf7d0'
                };
            case 'pending':
                return {
                    backgroundColor: '#fef3c7',
                    color: '#f59e0b',
                    border: '1px solid #fed7aa'
                };
            case 'denied':
            case 'defaulted':
                return {
                    backgroundColor: '#fee2e2',
                    color: '#dc2626',
                    border: '1px solid #fecaca'
                };
            default:
                return {
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #d1d5db'
                };
        }
    };

    return (
        <div style={{ width: '100%' }}>
            {loading ? (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '60px',
                        flexDirection: 'column',
                        gap: '16px'
                    }}
                >
                    <Spinner size='big' />
                    <Typography variant='h6' color='textSecondary'>
                        Loading credits...
                    </Typography>
                </div>
            ) : (
                <>
                    {error && (
                        <Alert severity='error' sx={{ marginBottom: '20px', borderRadius: '12px' }}>
                            {error}
                        </Alert>
                    )}

                    <TableContainer
                        component={Paper}
                        sx={{
                            backgroundColor: '#ffffff',
                            borderRadius: '12px',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                            overflow: 'auto',
                            maxWidth: '100%'
                        }}
                    >
                        <Table sx={{ minWidth: 650, width: '100%', tableLayout: 'fixed' }}>
                            <TableHead
                                sx={{
                                    backgroundColor: '#085d40',
                                    borderBottom: '2px solid #064532'
                                }}
                            >
                                <TableRow>
                                    <TableCell sx={{ ...tableCellStyle, width: '5%' }}>
                                        No.
                                    </TableCell>
                                    <TableCell sx={{ ...tableCellStyle, width: '15%' }}>
                                        Name
                                    </TableCell>
                                    <TableCell sx={{ ...tableCellStyle, width: '15%' }}>
                                        Address
                                    </TableCell>
                                    <TableCell sx={{ ...tableCellStyle, width: '12%' }}>
                                        Amount
                                    </TableCell>
                                    <TableCell sx={{ ...tableCellStyle, width: '10%' }}>
                                        Period
                                    </TableCell>
                                    <TableCell sx={{ ...tableCellStyle, width: '8%' }}>
                                        Interest
                                    </TableCell>
                                    <TableCell sx={{ ...tableCellStyle, width: '12%' }}>
                                        Type
                                    </TableCell>
                                    <TableCell sx={{ ...tableCellStyle, width: '10%' }}>
                                        Status
                                    </TableCell>
                                    <TableCell sx={{ ...tableCellStyle, width: '13%' }}>
                                        Actions
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {currentCredits.length > 0 ? (
                                    currentCredits.map((credit, index) => (
                                        <TableRow
                                            key={credit._id}
                                            sx={{
                                                backgroundColor:
                                                    index % 2 === 0 ? '#ffffff' : '#f9fafb',
                                                '&:hover': {
                                                    backgroundColor: '#f3f4f6'
                                                }
                                            }}
                                        >
                                            <TableCell sx={customTableCellStyle}>
                                                {indexOfFirstItem + index + 1}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    ...customTableCellStyle,
                                                    fontWeight: 600,
                                                    color: '#1f2937'
                                                }}
                                            >
                                                {credit.name}
                                            </TableCell>
                                            <TableCell sx={customTableCellStyle}>
                                                {credit.address}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    ...customTableCellStyle,
                                                    fontFamily: 'monospace',
                                                    color: '#065f46',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {credit.amount?.toLocaleString('en-US')} UGX
                                            </TableCell>
                                            <TableCell sx={customTableCellStyle}>
                                                {credit.paymentPeriod} months
                                            </TableCell>
                                            <TableCell sx={customTableCellStyle}>
                                                {credit.interestRate}%
                                            </TableCell>
                                            <TableCell sx={customTableCellStyle}>
                                                {credit.creditType}
                                            </TableCell>
                                            <TableCell sx={customTableCellStyle}>
                                                <Chip
                                                    label={credit.status}
                                                    size='small'
                                                    sx={{
                                                        ...getStatusColor(credit.status),
                                                        fontWeight: 600,
                                                        fontSize: '11px'
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={customTableCellStyle}>
                                                <Box sx={{ display: 'flex', gap: '8px' }}>
                                                    <Button
                                                        size='small'
                                                        onClick={() => {
                                                            setSelectedCredit(credit);
                                                            setDetailsOpen(true);
                                                        }}
                                                        sx={{
                                                            backgroundColor: '#3b82f6',
                                                            color: '#ffffff',
                                                            fontSize: '11px',
                                                            padding: '4px 8px',
                                                            borderRadius: '6px',
                                                            textTransform: 'none',
                                                            '&:hover': {
                                                                backgroundColor: '#2563eb'
                                                            }
                                                        }}
                                                    >
                                                        Details
                                                    </Button>
                                                    <Button
                                                        size='small'
                                                        onClick={() => {
                                                            setSelectedCredit(credit);
                                                            setDelOpen(true);
                                                        }}
                                                        sx={{
                                                            backgroundColor: '#dc2626',
                                                            color: '#ffffff',
                                                            fontSize: '11px',
                                                            padding: '4px 8px',
                                                            borderRadius: '6px',
                                                            textTransform: 'none',
                                                            '&:hover': {
                                                                backgroundColor: '#b91c1c'
                                                            }
                                                        }}
                                                    >
                                                        Delete
                                                    </Button>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell
                                            colSpan={9}
                                            sx={{
                                                textAlign: 'center',
                                                padding: '40px',
                                                color: '#6b7280',
                                                fontSize: '16px'
                                            }}
                                        >
                                            {searchWord
                                                ? `No credits found matching "${searchWord}"`
                                                : 'No credits available'}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination */}
                    {!loading && credits.length > itemsPerPage && (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                marginTop: '20px',
                                padding: '20px',
                                backgroundColor: '#ffffff',
                                borderRadius: '12px',
                                border: '1px solid #e2e8f0',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                            }}
                        >
                            <Pagination
                                count={totalPages}
                                page={currentPage}
                                onChange={handlePageChange}
                                color='primary'
                                size='large'
                                sx={{
                                    '& .MuiPaginationItem-root': {
                                        color: '#1f2937',
                                        borderColor: '#e2e8f0',
                                        '&:hover': {
                                            backgroundColor: '#f3f4f6'
                                        }
                                    },
                                    '& .MuiPaginationItem-page.Mui-selected': {
                                        backgroundColor: '#085d40',
                                        color: '#ffffff'
                                    }
                                }}
                            />
                        </div>
                    )}
                </>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={delOpen}
                onClose={() => setDelOpen(false)}
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: '16px',
                        padding: '8px'
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        color: '#1f2937',
                        fontSize: '20px',
                        fontWeight: 600,
                        padding: '24px 24px 16px 24px'
                    }}
                >
                    Confirm Delete
                </DialogTitle>
                <DialogContent sx={{ padding: '20px 24px' }}>
                    <p style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '14px' }}>
                        Are you sure you want to delete "<strong>{selectedCredit?.name}</strong>"?
                        This action cannot be undone.
                    </p>
                    <p
                        style={{
                            margin: '0 0 12px 0',
                            color: '#374151',
                            fontSize: '14px',
                            fontWeight: 500
                        }}
                    >
                        Type "<strong>{selectedCredit?.name}</strong>" to confirm this action.
                    </p>
                    <TextField
                        fullWidth
                        placeholder={selectedCredit?.name}
                        value={confirmationText}
                        onChange={(e) => setConfirmationText(e.target.value)}
                        variant='outlined'
                        size='small'
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: '#ffffff',
                                borderRadius: '8px',
                                '& fieldset': {
                                    borderColor: '#d1d5db'
                                },
                                '&:hover fieldset': {
                                    borderColor: '#085d40'
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#085d40'
                                }
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ padding: '16px 24px 24px 24px' }}>
                    <Button
                        onClick={() => {
                            setDelOpen(false);
                            setConfirmationText('');
                        }}
                        sx={{
                            color: '#6b7280',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: '#f3f4f6'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDelete}
                        variant='contained'
                        disabled={deleting || confirmationText !== selectedCredit?.name}
                        sx={{
                            backgroundColor: '#dc2626',
                            color: '#ffffff',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: '#b91c1c'
                            },
                            '&.Mui-disabled': {
                                backgroundColor: '#9ca3af',
                                color: '#ffffff'
                            }
                        }}
                    >
                        {deleting ? <Spinner size='small' /> : 'Delete Credit'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Credit Details Dialog */}
            <Dialog
                open={detailsOpen}
                onClose={() => setDetailsOpen(false)}
                maxWidth='md'
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: '16px',
                        padding: '8px'
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        color: '#1f2937',
                        fontSize: '20px',
                        fontWeight: 600,
                        padding: '24px 24px 16px 24px',
                        borderBottom: '1px solid #e2e8f0'
                    }}
                >
                    Credit Details
                </DialogTitle>
                <DialogContent sx={{ padding: '24px' }}>
                    {selectedCredit && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <Box>
                                <Typography
                                    variant='subtitle2'
                                    sx={{
                                        color: '#6b7280',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}
                                >
                                    Name
                                </Typography>
                                <Typography
                                    variant='body1'
                                    sx={{ color: '#1f2937', fontWeight: 600, mb: 2 }}
                                >
                                    {selectedCredit.name}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    variant='subtitle2'
                                    sx={{
                                        color: '#6b7280',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}
                                >
                                    Address
                                </Typography>
                                <Typography
                                    variant='body1'
                                    sx={{ color: '#1f2937', fontWeight: 600, mb: 2 }}
                                >
                                    {selectedCredit.address}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    variant='subtitle2'
                                    sx={{
                                        color: '#6b7280',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}
                                >
                                    Amount
                                </Typography>
                                <Typography
                                    variant='body1'
                                    sx={{ color: '#065f46', fontWeight: 700, mb: 2 }}
                                >
                                    {selectedCredit.amount?.toLocaleString('en-US')} UGX
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    variant='subtitle2'
                                    sx={{
                                        color: '#6b7280',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}
                                >
                                    Payment Period
                                </Typography>
                                <Typography
                                    variant='body1'
                                    sx={{ color: '#1f2937', fontWeight: 600, mb: 2 }}
                                >
                                    {selectedCredit.paymentPeriod} months
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    variant='subtitle2'
                                    sx={{
                                        color: '#6b7280',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}
                                >
                                    Interest Rate
                                </Typography>
                                <Typography
                                    variant='body1'
                                    sx={{ color: '#1f2937', fontWeight: 600, mb: 2 }}
                                >
                                    {selectedCredit.interestRate}%
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    variant='subtitle2'
                                    sx={{
                                        color: '#6b7280',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}
                                >
                                    Credit Type
                                </Typography>
                                <Typography
                                    variant='body1'
                                    sx={{ color: '#1f2937', fontWeight: 600, mb: 2 }}
                                >
                                    {selectedCredit.creditType}
                                </Typography>
                            </Box>
                            <Box sx={{ gridColumn: '1 / -1' }}>
                                <Typography
                                    variant='subtitle2'
                                    sx={{
                                        color: '#6b7280',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}
                                >
                                    Current Status
                                </Typography>
                                <Chip
                                    label={selectedCredit.status}
                                    sx={{
                                        ...getStatusColor(selectedCredit.status),
                                        fontWeight: 600,
                                        fontSize: '12px',
                                        mt: 1
                                    }}
                                />
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions
                    sx={{
                        padding: '16px 24px 24px 24px',
                        borderTop: '1px solid #e2e8f0',
                        gap: '8px'
                    }}
                >
                    <Button
                        onClick={() => handleStatusChange('approved')}
                        sx={{
                            backgroundColor: '#059669',
                            color: '#ffffff',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: '#047857'
                            }
                        }}
                    >
                        Approve
                    </Button>
                    <Button
                        onClick={() => handleStatusChange('denied')}
                        sx={{
                            backgroundColor: '#dc2626',
                            color: '#ffffff',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: '#b91c1c'
                            }
                        }}
                    >
                        Deny
                    </Button>
                    <Button
                        onClick={() => handleStatusChange('disbursed')}
                        sx={{
                            backgroundColor: '#3b82f6',
                            color: '#ffffff',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: '#2563eb'
                            }
                        }}
                    >
                        Disburse
                    </Button>
                    <Button
                        onClick={() => setDetailsOpen(false)}
                        sx={{
                            color: '#6b7280',
                            textTransform: 'none',
                            marginLeft: '8px',
                            '&:hover': {
                                backgroundColor: '#f3f4f6'
                            }
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default CreditList;
